import { toaster } from "@/components/ui/toaster";
import { UnidadCotizacionServicio } from "@/constants/enums";
import { EstadosSolicitudServicio } from "@/constants/enums/status/entity-status-ids";
import { AppRoutes } from "@/constants/routes";
import {
  getSolicitudServicioDetail,
  SolicitudServicioDetail,
  updateSolicitudServicio,
} from "@/services/service-requests.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { FieldValues } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";

export const fetchSamplingRequestDetail = async (params: {
  id?: string;
  showLoad?: boolean;
  setLoadingDetail: (value: boolean) => void;
  setDetail: (value: SolicitudServicioDetail | null) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}) => {
  const { id, showLoad = true, setLoadingDetail, setDetail, navigate, fetchPermissions } =
    params;

  try {
    if (!id) return;
    if (showLoad) setLoadingDetail(true);
    const response = await getSolicitudServicioDetail(Number(id));
    setDetail(response);
  } catch (error) {
    const errorObj = error as AxiosError;
    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(errorObj, "No se pudo cargar la solicitud."),
      });
    }
    throw error;
  } finally {
    if (showLoad) setLoadingDetail(false);
  }
};

export const rejectSamplingRequest = async (params: {
  detailId?: number;
  idParam?: string;
  event?: FieldValues;
  setLoadingReject: (value: boolean) => void;
  setOpenRejectModal: (value: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  fetchDetail: (showLoad?: boolean) => Promise<void>;
}) => {
  const {
    detailId,
    idParam,
    event,
    setLoadingReject,
    setOpenRejectModal,
    navigate,
    fetchPermissions,
    fetchDetail,
  } = params;

  if (!detailId) return;
  setLoadingReject(true);

  try {
    await updateSolicitudServicio({
      id: Number(detailId),
      idEstado: EstadosSolicitudServicio.Rechazada,
      motivoRechazo: event?.request_detail,
    });

    toaster.create({
      type: "success",
      title: "Solicitud de muestreo rechazada exitosamente.",
    });

    await fetchDetail(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.SAMPLING_REQUEST_DETAIL.replace(":id", String(idParam)),
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo rechazar la solicitud.",
      });
    }
    throw error;
  } finally {
    setLoadingReject(false);
    setOpenRejectModal(false);
  }
};

export const markDoneSamplingRequest = async (params: {
  detailId?: number;
  unidadCotizacion?: number | string | null;
  idParam?: string;
  values: { quantity: number; fechaRealizacion: string };
  setLoadingDone: (value: boolean) => void;
  setOpenDoneModal: (value: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  fetchDetail: (showLoad?: boolean) => Promise<void>;
}) => {
  const {
    detailId,
    unidadCotizacion,
    idParam,
    values,
    setLoadingDone,
    setOpenDoneModal,
    navigate,
    fetchPermissions,
    fetchDetail,
  } = params;

  if (!detailId) return;
  setLoadingDone(true);

  try {
    const isMuestras =
      Number(unidadCotizacion) === UnidadCotizacionServicio.CantidadMuestra;

    await updateSolicitudServicio({
      id: Number(detailId),
      idEstado: EstadosSolicitudServicio.Realizado,
      fechaRealizacion: values.fechaRealizacion,
      cantidadMuestrasEjecutadas: isMuestras ? Number(values.quantity) : undefined,
      hectareasEjecutadas: isMuestras ? undefined : Number(values.quantity),
    });

    toaster.create({
      type: "success",
      title: "Solicitud de muestreo realizada exitosamente.",
    });

    await fetchDetail(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.SAMPLING_REQUEST_DETAIL.replace(":id", String(idParam)),
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo marcar como realizada la solicitud de muestreo.",
      });
    }
    throw error;
  } finally {
    setLoadingDone(false);
    setOpenDoneModal(false);
  }
};

export const closeSamplingRequest = async (params: {
  detailId?: number;
  idParam?: string;
  setLoadingClose: (value: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  fetchDetail: (showLoad?: boolean) => Promise<void>;
}) => {
  const {
    detailId,
    idParam,
    setLoadingClose,
    navigate,
    fetchPermissions,
    fetchDetail,
  } = params;

  if (!detailId) return;
  setLoadingClose(true);

  try {
    await updateSolicitudServicio({
      id: Number(detailId),
      idEstado: EstadosSolicitudServicio.Cerrado,
    });

    toaster.create({
      type: "success",
      title: "Solicitud de muestreo cerrada exitosamente.",
    });

    await fetchDetail(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.SAMPLING_REQUEST_DETAIL.replace(":id", String(idParam)),
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cerrar la solicitud.",
      });
    }
    throw error;
  } finally {
    setLoadingClose(false);
  }
};
